import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient, describeApiError } from '../services/api';
import { SalesStartProgress } from './SalesStartProgress';
import { CatalogProgress } from './CatalogProgress';
import { OpenDoorCountdown } from './OpenDoorCountdown';
import { PaymentRestrictionsModal } from './PaymentRestrictionsModal';
import type { NFTItem, Character } from '../types';
import { stepToRarity, type RarityKey } from '../utils/nftUtils';
import { useCatalogFilter } from '../contexts/CatalogFilterContext';
import { useUser } from '../contexts/UserContext';
import { penkiPerDayForNft } from '../utils/penki';
import { PenkiBadge, PenkiChip } from './PenkiBadge';
import { PenkiRateEntry, PenkiRateModal } from './PenkiRateModal';
import './HomePage.css';

const CATALOG_PER_PAGE = 51;

/** Показываем модалку об ограничениях оплаты один раз за сессию (сбрасывается при перезапуске Mini App). */
const PAYMENT_RESTRICTIONS_SESSION_KEY = 'catalog_payment_restrictions_shown';

// ── Filter data ───────────────────────────────────────────────────────────────

const FILTER_TIERS: { key: RarityKey; label: string; range: string; steps: number[] }[] = [
  { key: 'common',    label: 'Тир 4', range: '100-3,2 тыс. ₽',  steps: [100, 200, 400, 800, 1600, 3200] },
  { key: 'rare',      label: 'Тир 3', range: '6,4-25,6 тыс. ₽', steps: [6400, 12800, 25600] },
  { key: 'epic',      label: 'Тир 2', range: '50-200 тыс. ₽',  steps: [51200, 102400, 204800] },
  { key: 'legendary', label: 'Тир 1', range: '300-500 тыс. ₽', steps: [300000, 400000, 500000] },
];

/** Маппинг step_price -> step (синхрон с backend Nft::STEP_PRICES) */
const STEP_PRICE_TO_STEP: Record<number, number> = {
  100: 1, 200: 2, 400: 3, 800: 4, 1600: 5, 3200: 6,
  6400: 7, 12800: 8, 25600: 9,
  50000: 10, 100000: 11, 200000: 12,
  51200: 10, 102400: 11, 204800: 12, // epic tier (альтернативные значения в UI)
  300000: 13, 400000: 14, 500000: 15,
};

// ── Component ─────────────────────────────────────────────────────────────────

const RARITY_KEYS: RarityKey[] = ['common', 'rare', 'epic', 'legendary'];

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { onboardingStatus } = useUser();
  const [allNFTs, setAllNFTs] = useState<NFTItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const {
    selectedPrices,
    setSelectedPrices,
    selectedCharacterIds,
    setSelectedCharacterIds,
    selectedPrizeFilter,
    setSelectedPrizeFilter,
  } = useCatalogFilter();

  // Filter state (лист и черновик — локально; применённые фильтры — в CatalogFilterContext)
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [expandedTiers, setExpandedTiers] = useState<Set<RarityKey>>(new Set());
  const [tempPrices, setTempPrices] = useState<Map<RarityKey, Set<number>>>(new Map());
  const [tempCharacterIds, setTempCharacterIds] = useState<Set<number>>(new Set());
  const [tempPrizeFilter, setTempPrizeFilter] = useState<'all' | 'with_prizes' | 'without_prizes'>('all');

  // Character filter state
  const [characters, setCharacters] = useState<Character[]>([]);
  const [isCharacterExpanded, setIsCharacterExpanded] = useState(false);
  const [isPrizeExpanded, setIsPrizeExpanded] = useState(false);

  // Pagination (подгрузка по 51 при прокрутке)
  const [displayedCount, setDisplayedCount] = useState(CATALOG_PER_PAGE);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Banner: SalesStartProgress до окончания White List, CatalogProgress после
  const [isAfterWhiteListEnd, setIsAfterWhiteListEnd] = useState<boolean | null>(null);
  const [whiteListStatus, setWhiteListStatus] = useState<{ in_white_list: boolean } | null>(null);
  const [wlStats, setWlStats] = useState<{ total_spots: number; remaining_spots: number; white_list_start_date: string } | null>(null);
  const [opendoorStartDate, setOpendoorStartDate] = useState<Date | null>(null);
  const [opendoorCountdownExpired, setOpendoorCountdownExpired] = useState(false);

  // Модалка об ограничениях оплаты через СБП (показ один раз за сессию)
  const [showPaymentRestrictions, setShowPaymentRestrictions] = useState(false);
  const [ratesOpen, setRatesOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(PAYMENT_RESTRICTIONS_SESSION_KEY) === '1') {
      return;
    }
    // Онбординг-статус уже загружен один раз в App.tsx и лежит в UserContext —
    // не делаем повторный запрос /user/onboarding-status здесь.
    if (!onboardingStatus) {
      return;
    }
    // Показываем модалку об ограничениях только после привязки СБП: на самом
    // первом входе сначала должна пройти модалка привязки, а эта — при первом
    // заходе в каталог уже с привязанным СБП.
    if (!onboardingStatus.sbp_completed) {
      return;
    }
    sessionStorage.setItem(PAYMENT_RESTRICTIONS_SESSION_KEY, '1');
    setShowPaymentRestrictions(true);
  }, [onboardingStatus]);

  const closePaymentRestrictions = useCallback(() => {
    setShowPaymentRestrictions(false);
  }, []);

  // ── NFT handlers ───────────────────────────────────────────────────────────

  const handleNFTClick = useCallback((nft: NFTItem) => {
    navigate(`/nft/${nft.id}`);
  }, [navigate]);

  const handleRandomNFT = useCallback(async () => {
    try {
      const detail = await apiClient.getRandomNFT({ excludeOwned: true });
      navigate(`/nft/${detail.id}?random=1`);
    } catch {
      // ignore
    }
  }, [navigate]);

  // ── Filter handlers ────────────────────────────────────────────────────────

  const openFilter = () => {
    const copy = new Map<RarityKey, Set<number>>();
    selectedPrices.forEach((v, k) => copy.set(k, new Set(v)));
    setTempPrices(copy);
    setTempCharacterIds(new Set(selectedCharacterIds));
    setTempPrizeFilter(selectedPrizeFilter);
    setExpandedTiers(new Set());
    setIsCharacterExpanded(false);
    setIsPrizeExpanded(false);
    setIsFilterOpen(true);
  };

  const closeFilter = () => setIsFilterOpen(false);

  const toggleExpandTier = (key: RarityKey) => {
    setExpandedTiers(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleTempPrice = (tierKey: RarityKey, price: number) => {
    setTempPrices(prev => {
      const next = new Map(prev);
      const prices = new Set(next.get(tierKey) || []);
      if (prices.has(price)) prices.delete(price);
      else prices.add(price);
      if (prices.size === 0) next.delete(tierKey);
      else next.set(tierKey, prices);
      return next;
    });
  };

  const toggleTempCharacter = (id: number) => {
    const numId = Number(id);
    setTempCharacterIds(prev => {
      const next = new Set(prev);
      if (next.has(numId)) next.delete(numId);
      else next.add(numId);
      return next;
    });
  };

  const applyFilter = () => {
    const copy = new Map<RarityKey, Set<number>>();
    tempPrices.forEach((v, k) => copy.set(k, new Set(v)));
    setSelectedPrices(copy);
    setSelectedCharacterIds(new Set(tempCharacterIds));
    setSelectedPrizeFilter(tempPrizeFilter);
    setIsFilterOpen(false);
  };

  const resetFilters = () => {
    setSelectedPrices(new Map());
    setSelectedCharacterIds(new Set());
    setSelectedPrizeFilter('all');
    setTempPrices(new Map());
    setTempCharacterIds(new Set());
    setTempPrizeFilter('all');
    setIsFilterOpen(false);
  };

  const loadMoreItems = useCallback(() => {
    setDisplayedCount(prev => prev + CATALOG_PER_PAGE);
  }, []);

  // ── Derived state (нужен до useEffect с hasMore) ────────────────────────────

  const activeTierKeys = new Set(
    Array.from(selectedPrices.entries())
      .filter(([, prices]) => prices.size > 0)
      .map(([key]) => key)
  );

  const filteredNFTs = allNFTs.filter(nft => {
    const nftRarity = stepToRarity(nft.step, nft.tier);

    if (selectedCharacterIds.size > 0 && (!nft.character_id || !selectedCharacterIds.has(Number(nft.character_id)))) {
      return false;
    }

    if (selectedPrizeFilter === 'with_prizes' && !nft.has_active_prize_drop) {
      return false;
    }
    if (selectedPrizeFilter === 'without_prizes' && nft.has_active_prize_drop) {
      return false;
    }

    if (activeTierKeys.size > 0) {
      if (!activeTierKeys.has(nftRarity)) {
        return false;
      }
      const tierPrices = selectedPrices.get(nftRarity);
      if (tierPrices && tierPrices.size > 0) {
        const selectedSteps = new Set(
          Array.from(tierPrices).map(p => STEP_PRICE_TO_STEP[p]).filter((s): s is number => s != null)
        );
        const nftStep = nft.step ?? (nft.step_price != null ? STEP_PRICE_TO_STEP[nft.step_price] : undefined);
        if (nftStep == null || !selectedSteps.has(nftStep)) {
          return false;
        }
      }
    }

    return true;
  });

  const displayedNFTs = filteredNFTs.slice(0, displayedCount);
  const hasMore = displayedCount < filteredNFTs.length;

  // Сброс счётчика при смене фильтров или обновлении списка
  useEffect(() => {
    setDisplayedCount(CATALOG_PER_PAGE);
  }, [selectedPrices, selectedCharacterIds, selectedPrizeFilter, allNFTs]);

  // Infinite scroll: подгрузка при прокрутке
  useEffect(() => {
    if (!hasMore || loading) return;
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreItems();
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMoreItems]);

  // ── Data loading ───────────────────────────────────────────────────────────

  useEffect(() => {
    loadNFTs();
  }, []);

  useEffect(() => {
    const checkWhiteListEnd = async () => {
      try {
        const [wl, status] = await Promise.all([
          apiClient.getWhiteListStats().catch(() => null),
          apiClient.getWhiteListStatus().catch(() => null),
        ]);
        if (status) setWhiteListStatus(status);
        if (wl) {
          setWlStats({
            total_spots: wl.total_spots,
            remaining_spots: wl.remaining_spots,
            white_list_start_date: wl.white_list_start_date,
          });
        }
        if (wl?.opendoor_start_date) setOpendoorStartDate(new Date(wl.opendoor_start_date));
        if (wl?.white_list_start_date) {
          const endTime = new Date(wl.white_list_start_date).getTime();
          setIsAfterWhiteListEnd(Date.now() >= endTime);
        } else {
          setIsAfterWhiteListEnd(false); // нет даты — показываем SalesStartProgress
        }
      } catch {
        setIsAfterWhiteListEnd(false);
      }
    };
    checkWhiteListEnd();
  }, []);

  // Apply initial filter from URL (?rarity=common|rare|epic|legendary) — e.g. from "Купить" on Дропы
  useEffect(() => {
    const rarityParam = searchParams.get('rarity');
    if (!rarityParam || !RARITY_KEYS.includes(rarityParam as RarityKey)) return;
    const key = rarityParam as RarityKey;
    const tierConfig = FILTER_TIERS.find(t => t.key === key);
    if (!tierConfig) return;
    setSelectedPrices(prev => {
      const next = new Map(prev);
      next.set(key, new Set(tierConfig.steps));
      return next;
    });
  }, [searchParams]);

  const loadNFTs = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await apiClient.getNFTs();
      setAllNFTs(response.data || []);
      // Character directory is bundled in the catalog response (used by the filter).
      setCharacters(Array.isArray(response.characters) ? response.characters : []);
    } catch (error) {
      setLoadError(describeApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const activeFilterCount = Array.from(selectedPrices.values())
    .reduce((sum, prices) => sum + prices.size, 0)
    + selectedCharacterIds.size
    + (selectedPrizeFilter !== 'all' ? 1 : 0);

  // ── JSX ───────────────────────────────────────────────────────────────────

  return (
    <div className="page-container home-page">
      <PenkiBadge />

      {/* Progress Section: до окончания White List — "До старта продаж", после — CatalogProgress или таймер OpenDoor */}
      {isAfterWhiteListEnd === true ? (
        whiteListStatus?.in_white_list === true ? (
          <CatalogProgress />
        ) : whiteListStatus && !whiteListStatus.in_white_list && opendoorStartDate && Date.now() < opendoorStartDate.getTime() && !opendoorCountdownExpired ? (
          <OpenDoorCountdown
            targetDate={opendoorStartDate.toISOString()}
            onExpired={() => setOpendoorCountdownExpired(true)}
          />
        ) : (
          <CatalogProgress />
        )
      ) : isAfterWhiteListEnd === false ? (
        <SalesStartProgress wlStats={wlStats} />
      ) : null}

      <PenkiRateEntry onClick={() => setRatesOpen(true)} />
      <PenkiRateModal isOpen={ratesOpen} onClose={() => setRatesOpen(false)} />

      {/* NFT Catalog */}
      {loading ? (
        <div className="catalog-loading">
          <div className="catalog-spinner" />
        </div>
      ) : loadError ? (
        <div className="catalog-empty catalog-error">
          <p>{loadError}</p>
        </div>
      ) : displayedNFTs.length === 0 ? (
        <div className="catalog-empty">
          <p>{allNFTs.length === 0 ? 'NFT пока недоступны' : 'Нет NFT по выбранным фильтрам'}</p>
        </div>
      ) : (
        <>
          <div className="catalog-grid">
            {displayedNFTs.map(nft => (
              <div
                key={nft.id}
                className="catalog-nft-card"
                onClick={() => handleNFTClick(nft)}
              >
                {nft.is_purchase_locked && (
                  <span className="nft-badge badge-purchase-locked">Идёт покупка</span>
                )}
                {nft.has_active_prize_drop && (
                  <img src="/prizebadge.png" alt="Призовая" className="catalog-nft-prize-badge" />
                )}
                <div className="catalog-nft-image-container">
                  {(nft.preview_image_url ?? nft.image_url) ? (
                    <img
                      src={nft.preview_image_url ?? nft.image_url}
                      alt={nft.title}
                      className="catalog-nft-image"
                      loading="lazy"
                    />
                  ) : (
                    <div className="catalog-nft-placeholder">&#128444;</div>
                  )}
                  <PenkiChip className="nft-card-penki" value={penkiPerDayForNft(nft)} compact />
                </div>
                <div className="catalog-nft-info">
                  <span className="catalog-nft-price">
                    {nft.price > 0 ? `${nft.price.toLocaleString('ru-RU')} ₽` : 'Бесплатно'}
                  </span>
                  <h3 className="catalog-nft-title">{nft.title}</h3>
                </div>
              </div>
            ))}
          </div>
          {hasMore && (
            <div ref={loadMoreRef} className="catalog-loading">
              <div className="catalog-spinner" />
            </div>
          )}
        </>
      )}

      {/* Floating Buttons Row */}
      <div className="catalog-btn-row">
        <button className="catalog-random-btn button--accent" onClick={handleRandomNFT}>
          <span className="catalog-random-btn-icon" />
          Выбрать что-то случайное
        </button>
        <button
          className={`catalog-filter-btn ${activeFilterCount > 0 ? 'has-active' : ''}`}
          onClick={openFilter}
          aria-label="Фильтры"
        >
          <img src="/filter.png" alt="" className="catalog-filter-icon" />
          {activeFilterCount > 0 && (
            <span className="catalog-filter-badge">{activeFilterCount}</span>
          )}
        </button>
      </div>

      {/* ── Filter bottom sheet ── */}
      {isFilterOpen && (
        <div className="filter-overlay" onClick={closeFilter}>
          <div className="filter-sheet" onClick={e => e.stopPropagation()}>

            <div className="filter-header">
              <h3 className="filter-title">Фильтры</h3>
              <button className="filter-close-btn" onClick={closeFilter}>✕</button>
            </div>

            <div className="filter-list">
              {FILTER_TIERS.map(tier => {
                const tierPrices = tempPrices.get(tier.key) || new Set<number>();
                const isExpanded = expandedTiers.has(tier.key);
                const priceCount = tierPrices.size;
                return (
                  <div key={tier.key} className={`filter-item-wrapper ${priceCount > 0 ? 'active' : ''}`}>
                    <div className="filter-item-header" onClick={() => toggleExpandTier(tier.key)}>
                      <span className="filter-item-label">
                        {tier.label}
                        <span className="filter-item-range"> ({tier.range})</span>
                      </span>
                      <div className="filter-item-right">
                        {priceCount > 0 && (
                          <span className="filter-item-badge">{priceCount}</span>
                        )}
                        <img
                          src="/down.png"
                          alt=""
                          className={`filter-item-chevron ${isExpanded ? 'expanded' : ''}`}
                        />
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="filter-item-steps">
                        {tier.steps.map(price => (
                          <button
                            key={price}
                            className={`filter-step-chip ${tierPrices.has(price) ? 'selected' : ''}`}
                            onClick={() => toggleTempPrice(tier.key, price)}
                          >
                            {price.toLocaleString('ru-RU')} ₽
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Персонаж */}
              <div className={`filter-item-wrapper ${tempCharacterIds.size > 0 ? 'active' : ''}`}>
                  <div className="filter-item-header" onClick={() => setIsCharacterExpanded(prev => !prev)}>
                    <span className="filter-item-label">Персонаж</span>
                    <div className="filter-item-right">
                      {tempCharacterIds.size > 0 && (
                        <span className="filter-item-badge">{tempCharacterIds.size}</span>
                      )}
                      <img
                        src="/down.png"
                        alt=""
                        className={`filter-item-chevron ${isCharacterExpanded ? 'expanded' : ''}`}
                      />
                    </div>
                  </div>
                  {isCharacterExpanded && (
                    <div className="filter-item-steps">
                      {characters.length > 0 ? (
                        characters.map(ch => (
                          <button
                            key={ch.id}
                            className={`filter-step-chip ${tempCharacterIds.has(Number(ch.id)) ? 'selected' : ''}`}
                            onClick={() => toggleTempCharacter(Number(ch.id))}
                          >
                            {ch.name}
                          </button>
                        ))
                      ) : (
                        <p className="filter-empty-hint">Нет персонажей</p>
                      )}
                    </div>
                  )}
                </div>

              {/* Призовые NFT */}
              <div className={`filter-item-wrapper ${tempPrizeFilter !== 'all' ? 'active' : ''}`}>
                <div className="filter-item-header" onClick={() => setIsPrizeExpanded(prev => !prev)}>
                  <span className="filter-item-label">Призовые NFT</span>
                  <div className="filter-item-right">
                    {tempPrizeFilter !== 'all' && (
                      <span className="filter-item-badge">1</span>
                    )}
                    <img
                      src="/down.png"
                      alt=""
                      className={`filter-item-chevron ${isPrizeExpanded ? 'expanded' : ''}`}
                    />
                  </div>
                </div>
                {isPrizeExpanded && (
                  <div className="filter-item-steps">
                    <button
                      className={`filter-step-chip ${tempPrizeFilter === 'all' ? 'selected' : ''}`}
                      onClick={() => setTempPrizeFilter('all')}
                    >
                      Все
                    </button>
                    <button
                      className={`filter-step-chip ${tempPrizeFilter === 'with_prizes' ? 'selected' : ''}`}
                      onClick={() => setTempPrizeFilter('with_prizes')}
                    >
                      С призами
                    </button>
                    <button
                      className={`filter-step-chip ${tempPrizeFilter === 'without_prizes' ? 'selected' : ''}`}
                      onClick={() => setTempPrizeFilter('without_prizes')}
                    >
                      Без призов
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="filter-actions">
              <button className="filter-apply-btn" onClick={applyFilter}>
                Применить
              </button>
              {Array.from(tempPrices.values()).some(s => s.size > 0) || tempCharacterIds.size > 0 || tempPrizeFilter !== 'all' ? (
                <button type="button" className="filter-cancel-btn" onClick={resetFilters}>
                  Сбросить фильтры
                </button>
              ) : (
                <button className="filter-cancel-btn" onClick={closeFilter}>
                  Отмена
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      <PaymentRestrictionsModal
        isOpen={showPaymentRestrictions}
        onClose={closePaymentRestrictions}
      />
    </div>
  );
};
